import {
  formatISO,
  startOfDay,
  format,
  addDays,
  isValid,
  parseISO,
  differenceInSeconds,
} from "date-fns";
import { parse, toSeconds } from "iso8601-duration";
import { ParseValueError } from "../errors";

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T/g;

export const formatModes = {
  KEEP_OFFSET: "KEEP_OFFSET",
  CONVERT_UTC: "CONVERT_UTC",
  FIXED_DAY: "FIXED_DAY",
};

export function to2DigitString(value) {
  if (value < 10) return "0" + value;
  return value.toString();
}

export function parseISOString(iso8601, formatMode = formatModes.KEEP_OFFSET) {
  if (!iso8601) return null;
  switch (formatMode) {
    case formatModes.KEEP_OFFSET:
    case formatModes.CONVERT_UTC:
      return new Date(iso8601);
    case formatModes.FIXED_DAY: {
      const match = [...iso8601.matchAll(ISO_DATE_PATTERN)][0];
      if (!match) return null;
      return new Date(match[1], parseInt(match[2]) - 1, match[3]);
    }
    default:
      return "";
  }
}

export function formatISOString(date, formatMode = formatModes.KEEP_OFFSET) {
  if (!date) return "";
  switch (formatMode) {
    case formatModes.KEEP_OFFSET:
      return formatISO(date);
    case formatModes.CONVERT_UTC:
      return date.toISOString();
    case formatModes.FIXED_DAY:
      return `${format(date, "yyyy")}-${format(date, "MM")}-${format(
        date,
        "dd"
      )}T00:00:00.000Z`;
    default:
      return "";
  }
}

export function timeOfDayAsSeconds(date) {
  return differenceInSeconds(startOfDay(date), date);
}

function isoDurationToDays(iso) {
  try {
    return toSeconds(parse(iso)) / 86400;
  } catch (_) {
    throw ParseValueError({ type: "date" });
  }
}

function parseOffsetDays(offset) {
  const numeric = parseInt(offset);
  if (!isNaN(numeric)) return numeric;
  return isoDurationToDays(offset);
}

export function parseDateValue(value, offset = 0) {
  if (!value) return null;
  const base = parseISOString(value, formatModes.FIXED_DAY);
  if (isValid(base)) return addDays(base, parseOffsetDays(offset));
  return addDays(
    startOfDay(new Date()),
    parseOffsetDays(value) + parseOffsetDays(offset)
  );
}

export function parseDatetimeValue(value, offset = 0) {
  if (!value) return null;
  const base = parseISO(value);
  if (isValid(base)) return addDays(base, parseOffsetDays(offset));
  return addDays(new Date(), parseOffsetDays(value) + parseOffsetDays(offset));
}

/**
 * Collection of datetime utility functions for parsing, formatting, and manipulating dates.
 * Includes support for ISO strings, date offsets, and various format modes.
 */
export const DatetimeUtils = {
  formatModes,
  to2DigitString,
  parseISOString,
  formatISOString,
  timeOfDayAsSeconds,
  parseDateValue,
  parseDatetimeValue,
};
